import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { UserAvatar } from '@/components/UserAvatar';

interface LeaderboardEntry {
  user_id: string;
  display_name: string;
  total_points: number;
  correct_winners: number;
  correct_scores: number;
  avatar_id?: number;
  rank?: number;
}

interface LeaderboardPreviewProps {
  entries: LeaderboardEntry[];
  currentUserId?: string;
  groupId: string;
}

const RANK_COLORS: Record<number, string> = {
  1: '#FFD700',
  2: '#C0C0C0',
  3: '#CD7F32',
};

export function LeaderboardPreview({ entries, currentUserId, groupId }: LeaderboardPreviewProps) {
  const navigate = useNavigate();
  const top3 = entries.slice(0, 3);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold tracking-tight">טבלה</h2>
        <Link
          to={`/group/${groupId}/leaderboard`}
          className="text-sm text-accent hover:underline"
        >
          הצג טבלה מלאה ←
        </Link>
      </div>

      {/* Card */}
      <div className="bg-card rounded-2xl border border-border p-4 md:p-5">
        {top3.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-2">אין נתוני דירוג עדיין</p>
        ) : (
          <div className="space-y-1">
            {top3.map((entry, idx) => {
              const rank = idx + 1;
              const isCurrentUser = entry.user_id === currentUserId;
              // Winner = anyone tied at the top score (not just rank 1 — ranking
              // breaks point ties arbitrarily, so mark all co-leaders). Skip when
              // nobody has scored yet.
              const topPoints = top3[0]?.total_points ?? 0;
              const isWinner = topPoints > 0 && entry.total_points === topPoints;
              return (
                <div
                  key={entry.user_id}
                  onClick={() => navigate(`/group/${groupId}/bets/${entry.user_id}`)}
                  className={`flex items-center h-10 md:h-12 gap-3 px-2 md:px-3 rounded-lg cursor-pointer hover:bg-card/80 transition-colors ${
                    isWinner
                      ? 'border-2 border-[#FFD700] bg-[#FFD700]/5 winner-pulse'
                      : isCurrentUser
                        ? 'bg-accent/5'
                        : ''
                  }`}
                >
                  {/* Rank number */}
                  <span
                    className="text-sm md:text-base font-bold w-5 text-center shrink-0"
                    style={{ color: RANK_COLORS[rank] || 'inherit' }}
                  >
                    {rank}
                  </span>

                  {/* Avatar */}
                  <UserAvatar
                    avatarId={entry.avatar_id ?? null}
                    displayName={entry.display_name}
                    size="sm"
                  />

                  {/* Name */}
                  <span className="flex-1 text-sm md:text-base font-medium truncate text-right">
                    {entry.display_name}
                  </span>

                  {/* Points */}
                  <span className="text-sm md:text-base font-bold text-accent shrink-0">
                    {entry.total_points}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default LeaderboardPreview;
